<template>
  <footer class="footer">
    <div class="container footer__container">
      <div>
        <h4
          class="bold"
          v-if="translate(column_1.title)"
        >
          {{ translate(column_1.title) }}
        </h4>

        <div class="footer__text">
          <address
            v-html="address"
            v-if="address"
          />

          <ul
            class="margin__t--30"
            v-if="phone || email"
          >
            <li v-if="phone">
              <a
                :href="`tel:${phone.replace(/[^\d.-]+/g, '')}`"
                class="link__color--white-prim link--naked"
              >
                {{ phone }}
              </a>
            </li>

            <li v-if="email">
              <a
                :href="`mailto:${email}`"
                class="link__color--white-prim"
              >
                {{ email }}
              </a>
            </li>
          </ul>

          <app-content
            class="margin__t--30"
            :content="translate(column_1.bank_account)"
          />
        </div>
      </div>

      <div>
        <h4
          class="footer__title"
          v-if="translate(column_2.title)"
        >
          {{ translate(column_2.title) }}
        </h4>

        <nav
          class="navigation"
          v-if="column_2.navigation?.length"
        >
          <ul class="navigation__ul">
            <li
              v-for="({ link }, index) in column_2.navigation"
              :key="`navigation_${index}`"
              class="navigation__li"
            >
              <app-link
                :link="link"
                class="navigation__a link--naked link__color--white"
              />
            </li>
          </ul>
        </nav>
      </div>

      <div>
        <h4
          class="footer__title"
          v-if="translate(column_3.title_1)"
        >
          {{ translate(column_3.title_1) }}
        </h4>

        <app-social-networks
          class="social-networks"
          :facebook="['fab', 'facebook-f']"
          :linkedin="['fab', 'linkedin-in']"
        />

        <h4
          class="footer__title margin__t--18"
          v-if="translate(column_3.title_2)"
        >
          {{ translate(column_3.title_2) }}
        </h4>

        <app-content
          class="newsletter margin__t--6"
          :content="translate(column_3.newsletter)"
        />
      </div>

      <div>
        <h4
          class="footer__title"
          v-if="translate(column_4.title)"
        >
          {{ translate(column_4.title) }}
        </h4>

        <app-link
          :link="column_4.cta"
          class="bold btn btn__color--prim btn__size--classic d-inline-block cta"
        />

        <app-content
          class="footer__text margin__t--18"
          :content="translate(column_4.text)"
        />

        <p
          class="twint margin__t--42"
          v-if="translate(twint.image)"
        >
          <a
            class="d-inline-block"
            target="_blank"
            :href="translate(twint.url)"
            :aria-label="translate(twint.alt)"
            v-if="translate(twint.url)"
          >
            <img
              class="footer__image"
              height="41"
              :src="translate(twint.image)"
              :alt="translate(twint.alt)"
            >
          </a>

          <img
            class="footer__image"
            height="41"
            :src="translate(twint.image)"
            :alt="translate(twint.alt)"
            v-else
          >
        </p>

        <p
          class="zewo margin__t--48"
          v-if="translate(zewo.image)"
        >
          <a
            class="d-inline-block"
            target="_blank"
            :href="translate(zewo.url)"
            :aria-label="translate(zewo.alt)"
            v-if="translate(zewo.url)"
          >
            <img
              class="footer__image"
              height="56"
              :src="translate(zewo.image)"
              :alt="translate(zewo.alt)"
            >
          </a>

          <img
            class="footer__image"
            height="56"
            :src="translate(zewo.image)"
            :alt="translate(zewo.alt)"
            v-else
          >
        </p>
      </div>
    </div>

    <div
      class="container legal"
      v-if="translate(column_5.text) || column_5.navigation?.length"
    >
      <nav class="legal-navigation">
        <template v-if="translate(column_5.text)">
          {{ interpolate(column_5.text, { year: new Date().getFullYear() }) }}
        </template>

        <ul
          class="legal-navigation__ul"
          v-if="column_5.navigation?.length"
        >
          <li
            v-for="({ link }, index) in column_5.navigation"
            :key="`legal-navigation_${index}`"
            class="legal-navigation__li"
          >
            <app-link
              :link="link"
              class="legal-navigation__a link--naked link__color--white"
            />
          </li>
        </ul>
      </nav>

      <p
        class="d-flex align-items-center actalliance"
        v-if="translate(actalliance.image)"
      >
        <span v-if="translate(actalliance.text)">
          {{ translate(actalliance.text) }}
        </span>

        <a
          class="d-inline-block"
          target="_blank"
          :href="translate(actalliance.url)"
          :aria-label="translate(actalliance.alt)"
          v-if="translate(actalliance.url)"
        >
          <img
            class="footer__image"
            height="24"
            :src="translate(actalliance.image)"
            :alt="translate(actalliance.alt)"
          >
        </a>

        <img
          class="footer__image"
          height="24"
          :src="translate(actalliance.image)"
          :alt="translate(actalliance.alt)"
          v-else
        >
      </p>
    </div>
  </footer>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'
import { useI18n } from '@/vendors/i18n'

import AppContent from '&/atoms/AppContent'
import AppSocialNetworks from '&/molecules/AppSocialNetworks'
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))

const { section } = defineProps({
  section: { type: Object, required: true }
})

const { translate, interpolate } = useI18n()

const { column_1 = {}, column_2 = {}, column_3 = {}, column_4 = {}, column_5 = {} } = section.attributes

const address = translate(column_1.address)?.replaceAll('\n', '<br/>')
const phone = translate(column_1.phone)
const email = translate(column_1.email)
const twint = column_4.twint
const zewo = column_4.zewo
const actalliance = column_5.actalliance
</script>

<style scoped lang="scss">
.footer {
  color: $white;
  background-color: #585858;
  padding: 24px 0;
  line-height: 2;

  @include mq(md) {
    padding-top: 100px;
  }

  a,
  :deep(a) {
    color: $white;

    &:hover {
      color: shade($white, 12%);
    }
  }

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px;

    @include mq(sm) {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mq(md) {
      grid-template-columns: repeat(4, 1fr);
    }

    @include mq(lg) {
      gap: 75px;
      grid-template-columns: repeat(4, auto);
    }

    @include mq(xl) {
      grid-template-columns: 250px 1fr 290px 250px;
    }
  }

  &__title {
    font-weight: bold;
    margin-bottom: 18px;
    line-height: 1.18;
  }

  &__text {
    font-size: rem(15px);
  }

  &__image {
    display: block;
    max-width: 100%;
    height: auto;
  }
}

.navigation {
  &__ul {
    line-height: 1.3;
    font-size: rem(15px);
  }

  &__li {
    &:not(:last-child) {
      margin-bottom: 6px;
    }
  }

  :deep() {
    .navigation {
      &__a {
        &:hover,
        &.is-active {
          text-decoration: underline;
        }
      }
    }
  }
}

:deep() {
  .networks {
    &__ul {
      gap: 6px;
    }

    &__li {
      width: auto;
      height: auto;
    }

    &__a {
      color: #B5B5B5;
      font-size: rem(20px);

      &:hover {
        color: $white;
      }
    }
  }
}

.newsletter {
  font-size: rem(14px);
}

:deep() {
  .cta {
    border-radius: 3px;
    font-size: rem(13px);
    white-space: nowrap;
    display: inline-block;
  }
}

.legal {
  margin-top: 24px;
  padding-top: 24px;
  position: relative;

  &::before {
    content: '';
    position:  absolute;
    width: 100vw;
    height: 1px;
    background: #5F5F5F;
    top: 0;
  }
}

.legal {
  line-height: 1.2;
  font-size: rem(15px);

  @include mq(sm) {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.legal-navigation {
  display: flex;
  gap: 6px;
  align-items: center;
  color: #BDBDBD;
  font-size: rem(15px);
  line-height: 1.2;

  &__ul {
    display: flex;
    align-items: center;
  }

  &__li {
    &:not(:last-child) {
      &::after {
        content: '|';
        margin: 0 6px;
      }
    }
  }

  :deep() {
    .legal-navigation {
      &__a {
        color: #BDBDBD;

        &:hover {
          color: $white;
        }
      }
    }
  }
}

.actalliance {
  margin-top: 12px;
  gap: 3px;

  @include mq(sm) {
    margin: 0;
  }
}

.twint,
.zewo {
  line-height: 1;
}
</style>